import { cn } from '@april9au/react-common/utils';

import type { CtaLink } from './cta-link';
import { CtaLinkButton } from './cta-link';

/**
 * Page-top banner: eyebrow, heading, lead, and up to two CTAs, with an
 * optional photo framed in its own card beside the text (never full-bleed
 * behind it — that's what keeps stock photography reading as art direction
 * rather than a listing photo). Reusable for the homepage as well as other
 * top-of-page moments (About, Listings, Contact) with a different
 * heading/CTA per page.
 */
export function HeroSection({
  id,
  eyebrow,
  heading,
  description,
  primaryCta,
  secondaryCta,
  backgroundImage,
  className,
}: {
  id?: string;
  eyebrow?: string;
  heading: string;
  description?: string;
  primaryCta?: CtaLink;
  secondaryCta?: CtaLink;
  backgroundImage?: string;
  className?: string;
}) {
  const textBlock = (
    <div>
      {eyebrow ? (
        <p className="hero__tagline text-eyebrow">
          <span>{eyebrow}</span>
        </p>
      ) : null}

      <h1 className="text-display">{heading}</h1>

      {description ? <p className="text-lead mt-6 max-w-[68ch]">{description}</p> : null}

      {primaryCta || secondaryCta ? (
        <div className="mt-10 flex flex-wrap gap-4">
          {primaryCta ? <CtaLinkButton cta={primaryCta} size="lg" /> : null}
          {secondaryCta ? <CtaLinkButton cta={secondaryCta} size="lg" variant="secondary" /> : null}
        </div>
      ) : null}
    </div>
  );

  return (
    <section id={id} className={cn('hero', className)}>
      <div className="hero__content container">
        {backgroundImage ? (
          // Photo lives inside its own framed card beside the text, never behind
          // it — that's what lets stock photography read as art direction
          // instead of a full-bleed listing photo.
          <div className="grid items-center gap-10 lg:grid-cols-2">
            {textBlock}
            <div className="relative min-h-80 overflow-hidden rounded-md lg:min-h-[28rem]">
              <img src={backgroundImage} alt="" className="absolute inset-0 size-full object-cover" />
            </div>
          </div>
        ) : (
          textBlock
        )}
      </div>
    </section>
  );
}
